import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import ComparisonTable from "./ComparisonTable";
import { SKILL_GAP_SECTION } from "../data/home-data";

export default function SkillGap() {
    return (
        <section
            className="relative py-20 overflow-hidden bg-gradient-to-b from-[#FFF5F0] via-white to-white"
            data-testid="skill-gap-section"
        >
            {/* Decorative blobs — shared across entire section */}
            <div className="absolute top-40 -left-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-20 bg-gradient-to-tr from-aiblue to-aisky pointer-events-none float-slow" />
            <div className="absolute bottom-40 -right-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-20 bg-gradient-to-bl from-aiorange to-aipeach pointer-events-none float-fast" />
            <div className="absolute top-1/3 right-0 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 bg-blue-500/15 mix-blend-multiply pointer-events-none" />
            <div className="absolute bottom-1/4 left-0 w-[700px] h-[700px] rounded-full blur-[140px] opacity-20 bg-orange-500/15 mix-blend-multiply pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
                {/* ═══════ SECTION 1: THE SKILL GAP ═══════ */}
                <div className="mb-14">
                    <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-3">
                        {SKILL_GAP_SECTION.badge}
                    </p>
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-normal leading-[1.05]">
                        {SKILL_GAP_SECTION.headline1}
                        <br />
                        <span className="text-aiblue">
                            {SKILL_GAP_SECTION.headline2}
                        </span>
                    </h2>
                    <p
                        className="max-w-3xl mt-5 text-lg font-bold text-gray-600"
                        dangerouslySetInnerHTML={{
                            __html: SKILL_GAP_SECTION.description,
                        }}
                    />
                </div>

                {/* Skill Gap Cards */}
                <div className="grid md:grid-cols-2 gap-7 items-stretch">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        whileHover={{ y: -4 }}
                        transition={{ duration: 0.4 }}
                        className="group rounded-[32px] p-8 lg:p-10 bg-aiblue text-white border border-white/10 shadow-[0_10px_40px_rgba(21,88,255,0.18)] relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
                        <div className="relative z-10">
                            <h3 className="font-display font-bold text-2xl lg:text-3xl mb-8">
                                What companies want
                            </h3>
                            <ul className="space-y-5">
                                {SKILL_GAP_SECTION.whatCompaniesWant.map(
                                    (w, i) => (
                                        <li
                                            key={i}
                                            className="flex gap-4 items-start"
                                        >
                                            <Check className="h-5 w-5 text-aiorange shrink-0 mt-0.5" />
                                            <span className="text-[15px] lg:text-base leading-7 text-white/95">
                                                {w}
                                            </span>
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        whileHover={{ y: -4 }}
                        transition={{ duration: 0.4 }}
                        className="rounded-[32px] p-8 lg:p-10 bg-aibeige border border-[#E8DFC8] shadow-[0_10px_35px_rgba(0,0,0,0.04)] relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent pointer-events-none" />
                        <div className="relative z-10">
                            <h3 className="font-display font-bold text-2xl lg:text-3xl text-gray-900 mb-8">
                                What colleges deliver
                            </h3>
                            <ul className="space-y-5">
                                {SKILL_GAP_SECTION.whatCollegesDeliver.map(
                                    (w, i) => (
                                        <li
                                            key={i}
                                            className="flex gap-4 items-start"
                                        >
                                            <X className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                                            <span className="text-[15px] lg:text-base leading-7 text-gray-700">
                                                {w}
                                            </span>
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom Statement */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="mt-10 max-w-4xl mx-auto rounded-[32px] border border-aiorange/20 bg-gradient-to-b from-aipeach/50 to-aipeach/30 px-8 py-7 text-center shadow-[0_10px_30px_rgba(255,153,0,0.05)]"
                >
                    <p className="text-lg leading-8 text-gray-800 font-medium">
                        {SKILL_GAP_SECTION.solution.text}
                    </p>
                </motion.div>

                {/* ═══════ SECTION 2: COMPARISON TABLE ═══════ */}
                <div className="mt-9">
                    <ComparisonTable />
                </div>
            </div>
        </section>
    );
}
